/** Orders that exist upstream but have not been paid.
 *
 *  WHY THIS EXISTS: order creation happens BEFORE the payment hand-off, so the
 *  moment /public/bookings returns a booking id the customer HAS a reservation.
 *  If payment then fails, is abandoned, or completes in another tab, the cart
 *  must not still contain a duplicate of it. We therefore persist the created
 *  order reference FIRST and clear the cart SECOND -- and surface the unpaid
 *  order so nobody is ever asked to rebuild a cart for a booking they already
 *  hold.
 *
 *  localStorage, not sessionStorage: an iframe-to-tab hop or a closed tab must
 *  not lose the reference.
 */
const KEY = "wbm.pending.v1";
const EVT = "wbm-pending-change";
const MAX_AGE_DAYS = 30;

const read = () => {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (rows) => {
  window.localStorage.setItem(KEY, JSON.stringify(rows));
  window.dispatchEvent(new Event(EVT));
};

const fresh = (rows) => {
  const cutoff = Date.now() - MAX_AGE_DAYS * 86400000;
  return rows.filter((r) => !r.createdAt || new Date(r.createdAt).getTime() > cutoff);
};

export const PENDING_EVENT = EVT;

export const listPendingOrders = () => fresh(read());

/** Called the instant an order is created, BEFORE the cart is cleared. */
export const recordPendingOrder = (order, cartLines = []) => {
  const code = order?.orderConfirmationCode;
  if (!code) return;
  const row = {
    code,
    bookingId: order.bookingId ?? null,
    orderTotal: order.orderTotal ?? null,
    orderDeposit: order.orderDeposit ?? null,
    payTodayAmount: order.orderDeposit > 0 ? order.orderDeposit : order.orderTotal ?? null,
    createdAt: new Date().toISOString(),
    // Enough to recognise the customer's OWN hold when a cart line stops pricing.
    lines: cartLines.map((l) => ({
      assetId: l.assetId,
      displayName: l.displayName,
      date: l.date,
      startTime: l.startTime,
      endTime: l.endTime,
    })),
  };
  write([row, ...fresh(read()).filter((r) => r.code !== code)]);
};

/** Payment confirmed BY THE VENDOR, or the order is gone. Only then forget it. */
export const clearPendingOrder = (code) => {
  if (!code) return;
  write(fresh(read()).filter((r) => r.code !== code));
};

/** Does this cart line refer to a slot the customer's own pending order holds? */
export const findSelfConflict = (line, pending = listPendingOrders()) => {
  if (!line?.assetId) return null;
  for (const order of pending) {
    const hit = (order.lines || []).some(
      (l) =>
        l.assetId === line.assetId &&
        l.date === line.date &&
        (!l.startTime || !line.startTime || l.startTime === line.startTime),
    );
    if (hit) return order;
  }
  return null;
};
